import React, { useCallback } from "react";
import { graphql, useLazyLoadQuery, useMutation } from "react-relay";
import { LazyQueryNetworkBoundrary } from "../../../../../components/mobile/QueryNetworkBoundrary";
import { UserLoggedInNavigatorScreenProps } from "../../../../../TopLevelNavigators/UserLoggedIn/UserLoggedInNavigatorParamList";

import { WidgetSizeT } from "../../localTypes/WidgetSizeT";
import { WASizePreview } from "../../WidgetAdd/WASizePreview/WASizePreview";

import {
  WAIntegrationTurnpikeQuery,
  WidgetIntegrationTurnpikeSize,
} from "./__generated__/WAIntegrationTurnpikeQuery.graphql";
import { WAIntegrationTurnpikeMutation } from "./__generated__/WAIntegrationTurnpikeMutation.graphql";

import { WidgetIntegrationTurnpike } from "widgets/src/WidgetIntegrationTurnpike/WidgetIntegrationTurnpike";
import { withWebModal } from "@/src/TopLevelNavigators/UserLoggedIn/withWebModal";
import { View } from "react-native";
import { WAFakeHeader } from "../../WidgetAdd/WAFakeHeader";
import { useSizeClass } from "../../../../../common/Utils/useSizeClass";
import { SizeClass } from "@/src/common/Utils/SizeClass";

type Props = UserLoggedInNavigatorScreenProps<"WidgetAddIntegrationTurnpike">;

export const WAIntegrationTurnpike = withWebModal(WAIntegrationTurnpikeC, true);
function WAIntegrationTurnpikeC(props: Props) {
  return (
    <LazyQueryNetworkBoundrary>
      {(fetchKey) => (
        <InnerWAIntegrationTurnpike {...props} fetchKey={fetchKey} />
      )}
    </LazyQueryNetworkBoundrary>
  );
}

function InnerWAIntegrationTurnpike(props: Props & { fetchKey: string }) {
  const [createWidgetIpool, loading] =
    useMutation<WAIntegrationTurnpikeMutation>(graphql`
      mutation WAIntegrationTurnpikeMutation(
        $storecastId: ID!
        $input: CreateWidgetIntegrationTurnpikeInput!
      ) {
        dashboard(dashboardId: $storecastId) {
          createWidgetIntegrationTurnpike(widget: $input) {
            id
          }
        }
      }
    `);

  const onCreate = useCallback(
    (size: WidgetSizeT) => {
      createWidgetIpool({
        variables: {
          storecastId: props.route.params.storecastId,
          input: {
            positionX:
              props.route.params.position.x + props.route.params.position.c * 2,
            positionY: props.route.params.position.y,
            size: size.h === 1 ? "TWO_X_ONE" : "TWO_X_TWO",
          },
        },
        onCompleted: () => props.navigation.pop(2),
      });
    },
    [
      createWidgetIpool,
      props.navigation,
      props.route.params.position.c,
      props.route.params.position.x,
      props.route.params.position.y,
      props.route.params.storecastId,
    ]
  );

  const isCompact = useSizeClass().width === SizeClass.COMPACT;

  return (
    <View style={{ flex: 1 }}>
      {isCompact ? null : <WAFakeHeader />}
      <WASizePreview
        fullScreen
        loading={loading}
        maxSize={props.route.params.maxSize}
        onCreate={onCreate}
        unitSize={props.route.params.unitSize}
        widgetSizes={[
          { w: 2, h: 1 },
          { w: 2, h: 2 },
        ]}
      >
        {(size) => {
          return (
            <Preview
              size={size.h === 1 ? "TWO_X_ONE" : "TWO_X_TWO"}
              fetchKey={props.fetchKey}
              storecastId={props.route.params.storecastId}
            />
          );
        }}
      </WASizePreview>
    </View>
  );
}

function Preview(props: {
  fetchKey: string;
  storecastId: string;
  size: WidgetIntegrationTurnpikeSize;
}) {
  const data = useLazyLoadQuery<WAIntegrationTurnpikeQuery>(
    graphql`
      query WAIntegrationTurnpikeQuery(
        $storecastId: ID!
        $input: PreviewWidgetTurnpikeIntegrationInput!
      ) {
        dashboard(id: $storecastId) @required(action: THROW) {
          previewWidgetTurnpikeIntegration(input: $input) {
            ...WidgetIntegrationTurnpike_widgetIntegrationTurnpike
          }
        }
      }
    `,
    {
      storecastId: props.storecastId,
      input: {
        size: props.size,
      },
    },
    { fetchKey: props.fetchKey, fetchPolicy: "store-and-network" }
  );

  if (!data.dashboard.previewWidgetTurnpikeIntegration) {
    return null;
  }

  return (
    <WidgetIntegrationTurnpike
      widget={data.dashboard.previewWidgetTurnpikeIntegration}
      storecastId={props.storecastId}
      interactive={false}
      mode="IDLE"
    />
  );
}
